import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { Plus, Minus, X, MessageCircle } from "lucide-react";
import { WHATSAPP_NUMBER } from "../lib/api";

const buildWhatsAppLink = (items, subtotal) => {
  const lines = items.map(
    (i) => `• ${i.name} × ${i.qty} — €${(i.price * i.qty).toFixed(2)}`
  );
  const text = [
    "Hola GALOLÀ! Me gustaría hacer este pedido:",
    "",
    ...lines,
    "",
    `Total: €${subtotal.toFixed(2)}`,
    "",
    "¿Me confirmas disponibilidad y envío? ✨",
  ].join("\n");
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
};

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-[#FAF9F5] w-full sm:max-w-md p-0 flex flex-col border-l border-[#E5E0D8]"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-6 border-b border-[#E5E0D8] text-left">
          <SheetTitle className="font-serif-editorial text-2xl tracking-wide">
            Tu cesta
          </SheetTitle>
          <p className="label-tracked text-[#5C5C5C]">
            {items.length === 0
              ? "Vacía"
              : `${items.length} ${items.length === 1 ? "pieza" : "piezas"}`}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <p className="font-serif-editorial text-2xl text-[#1E1E1E] mb-3">
                Aún no hay piezas
              </p>
              <p className="text-sm text-[#5C5C5C] max-w-xs">
                Explora la colección y añade tu pieza favorita.
              </p>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((it) => (
                <li
                  key={it.id}
                  className="flex gap-4"
                  data-testid={`cart-item-${it.id}`}
                >
                  <div className="w-20 h-24 bg-[#EDE8DD] flex-shrink-0 overflow-hidden">
                    <img
                      src={it.image_url}
                      alt={it.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-start">
                      <h4 className="font-serif-editorial text-lg leading-tight pr-2">
                        {it.name}
                      </h4>
                      <button
                        onClick={() => removeItem(it.id)}
                        data-testid={`remove-${it.id}`}
                        className="text-[#5C5C5C] hover:text-[#C27A62]"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="text-sm text-[#5C5C5C] mt-1">
                      €{it.price.toFixed(2)}
                    </div>
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      <div className="flex items-center gap-3 border border-[#E5E0D8]">
                        <button
                          className="px-2 py-1 hover:bg-[#EDE8DD]"
                          onClick={() => updateQty(it.id, it.qty - 1)}
                          data-testid={`decrease-${it.id}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm w-6 text-center">{it.qty}</span>
                        <button
                          className="px-2 py-1 hover:bg-[#EDE8DD]"
                          onClick={() => updateQty(it.id, it.qty + 1)}
                          data-testid={`increase-${it.id}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-sm font-medium">
                        €{(it.price * it.qty).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#E5E0D8] p-6 space-y-4 bg-[#F9F6F0]">
            <div className="flex items-center justify-between">
              <span className="label-tracked text-[#5C5C5C]">Subtotal</span>
              <span
                className="font-serif-editorial text-2xl"
                data-testid="cart-subtotal"
              >
                €{subtotal.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-[#5C5C5C] leading-relaxed">
              El pago se acuerda por WhatsApp. Te confirmaré disponibilidad,
              envío y método de pago.
            </p>
            <a
              href={buildWhatsAppLink(items, subtotal)}
              target="_blank"
              rel="noreferrer"
              className="btn-whatsapp"
              data-testid="checkout-whatsapp-btn"
            >
              <MessageCircle size={18} />
              Finalizar por WhatsApp
            </a>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
